/**
 * Size-First Variant Selector
 *
 * This script handles:
 * 1. Adding classes for CSS-based reordering (fallback for :has())
 * 2. Size-first selection logic
 * 3. Graying out unavailable colors based on selected size
 * 4. Auto-selecting first available color when size changes
 * 5. Showing tooltip when clicking unavailable colors
 */

(function() {
  'use strict';

  // Configuration
  var CONFIG = {
    sizeKeywords: ['size'],
    colorKeywords: ['color', 'colour'],
    unavailableMessage: 'This colour is not available in the selected size',
    selectSizeMessage: 'Please select a size first'
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Find all product sections
    var productSections = document.querySelectorAll('[data-section-type="product"]');

    for (var i = 0; i < productSections.length; i++) {
      initSizeFirstSelector(productSections[i]);
    }
  }

  function initSizeFirstSelector(container) {
    // Get variant JSON data
    var variantJsonEl = container.querySelector('[data-variant-json]');
    if (!variantJsonEl) return;

    var variants;
    try {
      variants = JSON.parse(variantJsonEl.innerHTML);
    } catch (e) {
      console.error('Error parsing variant JSON:', e);
      return;
    }

    // Find variant wrappers and add ordering classes
    var variantWrappers = container.querySelectorAll('.variant-wrapper[data-type="button"]');

    var sizeWrapper = null;
    var colorWrapper = null;
    var sizeFieldset = null;
    var colorFieldset = null;
    var sizeOptionIndex = null;
    var colorOptionIndex = null;

    for (var i = 0; i < variantWrappers.length; i++) {
      var wrapper = variantWrappers[i];
      var fieldset = wrapper.querySelector('.variant-input-wrap');
      if (!fieldset) continue;

      var handle = (fieldset.getAttribute('data-handle') || '').toLowerCase();
      var optionIndex = fieldset.getAttribute('data-index');

      var isSizeOption = false;
      for (var j = 0; j < CONFIG.sizeKeywords.length; j++) {
        if (handle.indexOf(CONFIG.sizeKeywords[j]) !== -1) {
          isSizeOption = true;
          break;
        }
      }

      var isColorOption = false;
      for (var k = 0; k < CONFIG.colorKeywords.length; k++) {
        if (handle.indexOf(CONFIG.colorKeywords[k]) !== -1) {
          isColorOption = true;
          break;
        }
      }

      if (isSizeOption) {
        wrapper.classList.add('size-option-wrapper');
        sizeWrapper = wrapper;
        sizeFieldset = fieldset;
        sizeOptionIndex = optionIndex;
      } else if (isColorOption) {
        wrapper.classList.add('color-option-wrapper');
        colorWrapper = wrapper;
        colorFieldset = fieldset;
        colorOptionIndex = optionIndex;
      }
    }

    // If we don't have both size and color, exit
    if (!sizeFieldset || !colorFieldset) {
      console.log('Size-first selector: Could not find both size and color options');
      return;
    }

    // Store references
    container._sizeFirstData = {
      variants: variants,
      sizeOptionIndex: sizeOptionIndex,
      colorOptionIndex: colorOptionIndex,
      sizeFieldset: sizeFieldset,
      colorFieldset: colorFieldset,
      colorWrapper: colorWrapper
    };

    // Make all size options visible and clickable
    ensureAllSizesVisible(sizeFieldset);

    // Get size and color inputs
    var sizeInputs = sizeFieldset.querySelectorAll('input[type="radio"]');

    // Check if a size is already selected
    var selectedSize = null;
    for (var i = 0; i < sizeInputs.length; i++) {
      if (sizeInputs[i].checked) {
        selectedSize = sizeInputs[i].value;
      }
    }

    // Initial state: apply grayed-out styling based on selected size
    updateColorAvailability(container, selectedSize, variants, sizeOptionIndex, colorOptionIndex, false);

    // Add event listeners for size changes
    for (var i = 0; i < sizeInputs.length; i++) {
      sizeInputs[i].addEventListener('change', createSizeChangeHandler(container, variants, sizeOptionIndex, colorOptionIndex));
    }

    // Add click handlers for unavailable colors
    var colorInputWrappers = colorFieldset.querySelectorAll('.variant-input');
    for (var i = 0; i < colorInputWrappers.length; i++) {
      colorInputWrappers[i].addEventListener('click', createColorClickHandler(colorInputWrappers[i]), true);
    }
  }

  function createSizeChangeHandler(container, variants, sizeOptionIndex, colorOptionIndex) {
    return function() {
      var newSize = this.value;
      updateColorAvailability(container, newSize, variants, sizeOptionIndex, colorOptionIndex, true);
    };
  }

  function createColorClickHandler(wrapper) {
    return function(e) {
      if (wrapper.classList.contains('color-unavailable')) {
        e.preventDefault();
        e.stopPropagation();
        var colorName = wrapper.getAttribute('data-value') || 'This colour';
        colorName = colorName.replace(' (Limited Edition)', '');
        showUnavailableMessage(wrapper, colorName + ' is not available in this size');
        return false;
      } else if (wrapper.classList.contains('size-not-selected')) {
        e.preventDefault();
        e.stopPropagation();
        showUnavailableMessage(wrapper, CONFIG.selectSizeMessage);
        return false;
      }
    };
  }

  function updateColorAvailability(container, selectedSize, variants, sizeOptionIndex, colorOptionIndex, autoSelectColor) {
    var data = container._sizeFirstData;
    var colorFieldset = data.colorFieldset;
    var colorWrapper = data.colorWrapper;
    var colorInputWrappers = colorFieldset.querySelectorAll('.variant-input');

    // Get the option number from index (e.g., "option1" -> 1)
    var sizeOptNum = parseInt(sizeOptionIndex.replace('option', ''), 10);
    var colorOptNum = parseInt(colorOptionIndex.replace('option', ''), 10);

    // Build a set of available colors for the selected size
    var availableColors = {};
    var availableColorsList = [];
    var firstAvailableColor = null;

    if (selectedSize) {
      for (var i = 0; i < variants.length; i++) {
        var variant = variants[i];
        var variantSize = variant['option' + sizeOptNum];
        if (variantSize === selectedSize && variant.available) {
          var variantColor = variant['option' + colorOptNum];
          if (!availableColors[variantColor]) {
            availableColors[variantColor] = true;
            availableColorsList.push(variantColor);
            if (!firstAvailableColor) {
              firstAvailableColor = variantColor;
            }
          }
        }
      }
    }

    // Get currently selected color
    var currentSelectedColor = null;
    for (var i = 0; i < colorInputWrappers.length; i++) {
      var input = colorInputWrappers[i].querySelector('input[type="radio"]');
      if (input && input.checked) {
        currentSelectedColor = colorInputWrappers[i].getAttribute('data-value');
      }
    }

    // Check if current color is available for the new size
    var currentColorAvailable = availableColors[currentSelectedColor] === true;

    // Update each color option
    for (var i = 0; i < colorInputWrappers.length; i++) {
      var wrapper = colorInputWrappers[i];
      var label = wrapper.querySelector('label');
      var colorValue = wrapper.getAttribute('data-value');

      // Remove existing classes
      wrapper.classList.remove('color-unavailable', 'size-not-selected', 'color-available');
      if (label) {
        label.classList.remove('disabled', 'color-swatch-unavailable');
      }

      // Remove any existing message
      var existingMsg = wrapper.querySelector('.unavailable-tooltip');
      if (existingMsg) {
        existingMsg.parentNode.removeChild(existingMsg);
      }

      if (!selectedSize) {
        wrapper.classList.add('size-not-selected');
        if (label) {
          label.classList.add('color-swatch-unavailable');
        }
      } else if (!availableColors[colorValue]) {
        wrapper.classList.add('color-unavailable');
        if (label) {
          label.classList.add('color-swatch-unavailable');
        }
      } else {
        wrapper.classList.add('color-available');
      }
    }

    // If current color is not available and we should auto-select, select the first available color
    if (autoSelectColor && selectedSize && !currentColorAvailable && firstAvailableColor) {
      for (var i = 0; i < colorInputWrappers.length; i++) {
        var colorValue = colorInputWrappers[i].getAttribute('data-value');
        if (colorValue === firstAvailableColor) {
          var input = colorInputWrappers[i].querySelector('input[type="radio"]');
          if (input) {
            input.checked = true;
            var changeEvent = document.createEvent('Event');
            changeEvent.initEvent('change', true, true);
            input.dispatchEvent(changeEvent);

            if (availableColorsList.length > 0) {
              showColorChangedNotification(colorWrapper, firstAvailableColor, selectedSize, availableColorsList);
            }
          }
        }
      }
    } else if (autoSelectColor && selectedSize && !currentColorAvailable && !firstAvailableColor) {
      hideColorChangedNotification(colorWrapper);
    } else if (autoSelectColor && currentColorAvailable) {
      hideColorChangedNotification(colorWrapper);
    }
  }

  function showColorChangedNotification(colorWrapper, selectedColor, selectedSize, availableColorsList) {
    if (availableColorsList.length === 0) {
      hideColorChangedNotification(colorWrapper);
      return;
    }

    // Shorten color names
    var shortList = [];
    for (var i = 0; i < availableColorsList.length; i++) {
      shortList.push(availableColorsList[i].replace(' (Limited Edition)', ''));
    }

    var message = '';
    if (shortList.length === 1) {
      message = 'Only available colour for size ' + selectedSize + ' is <strong>' + shortList[0] + '</strong>';
    } else if (shortList.length === 2) {
      message = 'Only available colours for size ' + selectedSize + ' are <strong>' + shortList[0] + '</strong> and <strong>' + shortList[1] + '</strong>';
    } else {
      var lastColor = shortList[shortList.length - 1];
      var otherColors = shortList.slice(0, -1).join(', ');
      message = 'Only available colours for size ' + selectedSize + ' are <strong>' + otherColors + '</strong> and <strong>' + lastColor + '</strong>';
    }

    var notification = colorWrapper.querySelector('.color-changed-notification');

    if (notification) {
      var textEl = notification.querySelector('.color-changed-notification__text');
      if (textEl) {
        textEl.innerHTML = message;
      }
      notification.classList.remove('fade-out');
    } else {
      notification = document.createElement('div');
      notification.className = 'color-changed-notification';
      notification.innerHTML = '<div class="color-changed-notification__content"><span class="color-changed-notification__text">' + message + '</span></div>';
      colorWrapper.appendChild(notification);
    }
  }

  function hideColorChangedNotification(colorWrapper) {
    var notification = colorWrapper.querySelector('.color-changed-notification');
    if (notification) {
      notification.classList.add('fade-out');
      setTimeout(function() {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 200);
    }
  }

  function ensureAllSizesVisible(sizeFieldset) {
    // Function to enable all size options
    function enableAllSizes() {
      var sizeInputs = sizeFieldset.querySelectorAll('.variant-input');
      for (var i = 0; i < sizeInputs.length; i++) {
        var input = sizeInputs[i];
        input.style.display = 'inline-block';
        input.style.visibility = 'visible';
        input.classList.remove('hide', 'hidden', 'disabled', 'is-disabled');

        var label = input.querySelector('label');
        if (label) {
          label.style.display = 'inline-block';
          label.style.visibility = 'visible';
          label.classList.remove('disabled', 'is-disabled', 'btn--disabled');
        }

        var radioInput = input.querySelector('input[type="radio"]');
        if (radioInput && radioInput.disabled) {
          radioInput.disabled = false;
          radioInput.classList.remove('disabled', 'is-disabled');
        }
      }
    }

    // Run initially
    enableAllSizes();

    // Re-run after a short delay to catch theme's changes
    setTimeout(enableAllSizes, 50);
    setTimeout(enableAllSizes, 150);
    setTimeout(enableAllSizes, 300);

    // Also add click handlers on labels to manually handle selection
    // This bypasses any disabled state on the radio buttons
    var sizeInputs = sizeFieldset.querySelectorAll('.variant-input');
    for (var i = 0; i < sizeInputs.length; i++) {
      (function(wrapper) {
        var label = wrapper.querySelector('label');
        var radioInput = wrapper.querySelector('input[type="radio"]');

        if (label && radioInput) {
          label.addEventListener('click', function(e) {
            // Always enable and select when label is clicked
            e.preventDefault();
            e.stopPropagation();

            // Enable this radio
            radioInput.disabled = false;
            radioInput.checked = true;

            // Dispatch change event
            var changeEvent = document.createEvent('Event');
            changeEvent.initEvent('change', true, true);
            radioInput.dispatchEvent(changeEvent);

            // Re-enable all sizes after a short delay
            setTimeout(enableAllSizes, 10);
            setTimeout(enableAllSizes, 100);
            setTimeout(enableAllSizes, 250);
          }, true);
        }
      })(sizeInputs[i]);
    }
  }

  function showUnavailableMessage(wrapper, message) {
    var existingMsg = document.querySelector('.unavailable-tooltip-active');
    if (existingMsg) {
      existingMsg.parentNode.removeChild(existingMsg);
    }

    var tooltip = document.createElement('div');
    tooltip.className = 'unavailable-tooltip unavailable-tooltip-active';
    tooltip.textContent = message;

    wrapper.style.position = 'relative';
    wrapper.appendChild(tooltip);

    setTimeout(function() {
      if (tooltip.parentNode) {
        tooltip.classList.add('fade-out');
        setTimeout(function() {
          if (tooltip.parentNode) {
            tooltip.parentNode.removeChild(tooltip);
          }
        }, 300);
      }
    }, 2500);
  }

})();
